import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './App.css';
import './AdminDashboard.css';
import AdminNavbar from './components/AdminNavbar';
import ThreeBackground from './components/ThreeBackground';
import { Analytics, Messages, Settings } from './components/AdminComponents';

const ADMIN_STATS = [
  { title: 'Total Projects', value: '8', icon: '🚀', color: '#00ffff', gradient: 'linear-gradient(135deg, #00ffff, #0080ff)', trend: '+12%' },
  { title: 'Certifications', value: '10', icon: '🏆', color: '#ff00ff', gradient: 'linear-gradient(135deg, #ff00ff, #ff8000)', trend: '+8%' },
  { title: 'Skills', value: '19', icon: '⚡', color: '#ffff00', gradient: 'linear-gradient(135deg, #ffff00, #ff8000)', trend: '+15%' },
  { title: 'Messages', value: '24', icon: '📧', color: '#00ff00', gradient: 'linear-gradient(135deg, #00ff00, #80ff00)', trend: '+5%' }
];

const RECENT_ACTIVITIES = [
  { action: 'New project added', item: 'FlashCard Generator', time: '2 hours ago', type: 'project' },
  { action: 'Certificate uploaded', item: 'AWS Cloud Practitioner', time: '1 day ago', type: 'cert' },
  { action: 'Message received', item: 'Contact form submission', time: '3 hours ago', type: 'message' },
  { action: 'Skill updated', item: 'React proficiency', time: '5 hours ago', type: 'skill' }
];

const PROJECTS_DATA = [
  { id: 1, title: 'FlashCard Generator', status: 'LIVE', views: 1250, lastUpdated: '2025-01-15' },
  { id: 2, title: 'Random Quote Generator', status: 'LIVE', views: 890, lastUpdated: '2025-01-10' },
  { id: 3, title: 'Color Palette Generator', status: 'LIVE', views: 650, lastUpdated: '2025-01-08' },
  { id: 4, title: 'Task Management System', status: 'Coming Soon', views: 0, lastUpdated: '2025-01-05' }
];

function AdminDashboard() {
  const [darkMode, setDarkMode] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showAddProject, setShowAddProject] = useState(false);
  const [showAddCertificate, setShowAddCertificate] = useState(false);
  const [showUpdateSkills, setShowUpdateSkills] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '',
    status: 'Coming Soon...',
    github: '',
    demo: '',
    features: ['', '', '']
  });
  const [newCertificate, setNewCertificate] = useState({
    name: '',
    issuer: '',
    month: '',
    year: '',
    photo: null
  });
  const [newSkill, setNewSkill] = useState({
    name: '',
    level: '',
    category: 'Programming Languages'
  });

  const StatCard = ({ stat, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 50, rotateX: -15 }}
      animate={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{ duration: 0.8, delay: index * 0.15, type: "spring", stiffness: 100 }}
      whileHover={{ 
        scale: 1.08, 
        y: -10, 
        rotateY: 5
      }}
      className="card admin-stat-card"
      style={{
        borderRadius: '20px',
        padding: '2.5rem',
        backdropFilter: 'blur(20px)',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer'
      }}
    >
      {/* Animated background gradient */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: stat.gradient,
          opacity: 0.05,
          borderRadius: '20px'
        }}
        animate={{ 
          background: [
            stat.gradient,
            `linear-gradient(225deg, ${stat.color}20, transparent)`,
            stat.gradient
          ]
        }}
        transition={{ duration: 4, repeat: Infinity }}
      />
      
      {/* Floating particles */}
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            width: '4px',
            height: '4px',
            background: stat.color,
            borderRadius: '50%',
            opacity: 0.6
          }}
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            opacity: [0.6, 0.2, 0.6]
          }}
          transition={{
            duration: 3 + i,
            repeat: Infinity,
            delay: i * 0.5
          }}
        />
      ))}
      
      {/* Icon with enhanced animation */}
      <motion.div
        style={{
          position: 'absolute',
          top: '15px',
          right: '15px',
          fontSize: '2.5rem',
          opacity: darkMode ? 0.4 : 0.3,
          filter: darkMode ? 'none' : 'grayscale(0.3)'
        }}
        animate={{ 
          rotate: [0, 15, -15, 0],
          scale: [1, 1.1, 1]
        }}
        transition={{ duration: 5, repeat: Infinity }}
      >
        {stat.icon}
      </motion.div>
      
      <div style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <h3 style={{ 
            margin: '0', 
            fontSize: '0.85rem',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            fontWeight: '600'
          }}>
            {stat.title}
          </h3>
          <motion.span
            style={{
              fontSize: '0.75rem',
              color: stat.trend.startsWith('+') ? '#00ff88' : '#ff4466',
              fontWeight: 'bold',
              background: stat.trend.startsWith('+') 
                ? 'rgba(0, 255, 136, 0.1)' 
                : 'rgba(255, 68, 102, 0.1)',
              padding: '0.2rem 0.5rem',
              borderRadius: '8px',
              border: `1px solid ${stat.trend.startsWith('+') ? '#00ff88' : '#ff4466'}30`
            }}
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {stat.trend}
          </motion.span>
        </div>
        
        <motion.div
          style={{ 
            fontSize: '3rem', 
            fontWeight: '800', 
            color: 'var(--text-primary)',
            lineHeight: '1',
            marginBottom: '0.5rem'
          }}
          animate={{ 
            scale: [1, 1.02, 1]
          }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          {stat.value}
        </motion.div>
        
        {/* Progress indicator */}
        <div style={{
          width: '100%',
          height: '3px',
          background: 'var(--card-bg)',
          opacity: 0.3,
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <motion.div
            style={{
              height: '100%',
              background: stat.gradient,
              borderRadius: '2px'
            }}
            initial={{ width: '0%' }}
            animate={{ width: '75%' }}
            transition={{ duration: 2, delay: index * 0.2 }}
          />
        </div>
      </div>
    </motion.div>
  );

  const ActivityItem = ({ activity, index }) => (
    <motion.div
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ x: 10, backgroundColor: 'rgba(0, 255, 255, 0.05)' }}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '1rem',
        borderRadius: '8px',
        border: '1px solid rgba(0, 255, 255, 0.2)',
        marginBottom: '0.8rem',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
    >
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        background: activity.type === 'project' ? 'linear-gradient(45deg, #00ffff, #0080ff)' :
                   activity.type === 'cert' ? 'linear-gradient(45deg, #ff00ff, #ff8000)' :
                   activity.type === 'message' ? 'linear-gradient(45deg, #00ff00, #80ff00)' :
                   'linear-gradient(45deg, #ffff00, #ff8000)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '1rem',
        fontSize: '1.2rem'
      }}>
        {activity.type === 'project' ? '🚀' : 
         activity.type === 'cert' ? '🏆' :
         activity.type === 'message' ? '📧' : '⚡'}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', marginBottom: '0.2rem' }}>
          {activity.action}
        </div>
        <div style={{ color: 'var(--accent-primary)', fontSize: '0.9rem' }}>
          {activity.item}
        </div>
      </div>
      <div style={{ color: 'var(--text-primary)', opacity: 0.6, fontSize: '0.8rem' }}>
        {activity.time}
      </div>
    </motion.div>
  );

  const ProjectRow = ({ project, index }) => (
    <motion.tr
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ backgroundColor: 'rgba(0, 255, 255, 0.05)' }}
      style={{
        borderBottom: '1px solid rgba(0, 255, 255, 0.2)',
        transition: 'all 0.3s ease'
      }}
    >
      <td style={{ padding: '1rem', color: 'var(--text-primary)' }}>{project.title}</td>
      <td style={{ padding: '1rem' }}>
        <span style={{
          padding: '0.3rem 0.8rem',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: 'bold',
          background: project.status === 'LIVE' ? 
            'linear-gradient(45deg, #00ff00, #00ffff)' : 
            'linear-gradient(45deg, #ffaa00, #ff6600)',
          color: '#000'
        }}>
          {project.status}
        </span>
      </td>
      <td style={{ padding: '1rem', color: 'var(--accent-primary)' }}>{project.views}</td>
      <td style={{ padding: '1rem', color: 'var(--text-primary)', opacity: 0.7 }}>{project.lastUpdated}</td>
      <td style={{ padding: '1rem' }}>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          style={{
            background: 'linear-gradient(45deg, #ff00ff, #ff8000)',
            border: 'none',
            color: '#ffffff',
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.8rem',
            fontWeight: 'bold'
          }}
        >
          Edit
        </motion.button>
      </td>
    </motion.tr>
  );

  const renderDashboard = () => (
    <div style={{ padding: '2rem' }}>
      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '2rem',
        marginBottom: '3rem'
      }}>
        {ADMIN_STATS.map((stat, index) => (
          <StatCard key={stat.title} stat={stat} index={index} />
        ))}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: window.innerWidth > 768 ? '1fr 1fr' : '1fr',
        gap: '2rem'
      }}>
        {/* Recent Activities */}
        <motion.div
          initial={{ opacity: 0, x: -50, rotateY: -15 }}
          animate={{ opacity: 1, x: 0, rotateY: 0 }}
          transition={{ duration: 1, type: "spring", stiffness: 80 }}
          className="card admin-activity-panel"
          style={{
            borderRadius: '20px',
            padding: '2.5rem',
            backdropFilter: 'blur(20px)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Animated header decoration */}
          <motion.div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: darkMode 
                ? 'linear-gradient(90deg, #00ffff, #ff00ff, #ffff00, #00ffff)'
                : 'linear-gradient(90deg, #3182ce, #805ad5, #3182ce)',
              backgroundSize: '200% 100%'
            }}
            animate={{ backgroundPosition: ['0% 0%', '200% 0%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
            <motion.div
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '12px',
                background: darkMode 
                  ? 'linear-gradient(45deg, #00ffff, #0080ff)'
                  : 'linear-gradient(45deg, #3182ce, #4299e1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem'
              }}
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
            >
              📊
            </motion.div>
            <h3 style={{
              color: 'var(--accent-primary)',
              margin: '0',
              fontSize: '1.4rem',
              fontWeight: '700',

            }}>
              Recent Activities
            </h3>
          </div>
          {RECENT_ACTIVITIES.map((activity, index) => (
            <ActivityItem key={index} activity={activity} index={index} />
          ))}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, x: 50, rotateY: 15 }}
          animate={{ opacity: 1, x: 0, rotateY: 0 }}
          transition={{ duration: 1, type: "spring", stiffness: 80, delay: 0.2 }}
          className="card admin-actions-panel"
          style={{
            borderRadius: '20px',
            padding: '2.5rem',
            backdropFilter: 'blur(20px)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Animated header decoration */}
          <motion.div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: darkMode 
                ? 'linear-gradient(90deg, #ff00ff, #8000ff, #ff00ff)'
                : 'linear-gradient(90deg, #805ad5, #9f7aea, #805ad5)',
              backgroundSize: '200% 100%'
            }}
            animate={{ backgroundPosition: ['200% 0%', '0% 0%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
            <motion.div
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '12px',
                background: darkMode 
                  ? 'linear-gradient(45deg, #ff00ff, #8000ff)'
                  : 'linear-gradient(45deg, #805ad5, #9f7aea)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem'
              }}
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              ⚡
            </motion.div>
            <h3 style={{
              color: 'var(--accent-secondary)',
              margin: '0',
              fontSize: '1.4rem',
              fontWeight: '700',

            }}>
              Quick Actions
            </h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              { title: 'Add New Project', icon: '🚀', color: '#00ffff', gradient: 'linear-gradient(45deg, #00ffff, #0080ff)' },
              { title: 'Upload Certificate', icon: '🏆', color: '#ff00ff', gradient: 'linear-gradient(45deg, #ff00ff, #8000ff)' },
              { title: 'Add Event Photos', icon: '📸', color: '#ff6b6b', gradient: 'linear-gradient(45deg, #ff6b6b, #ee5a24)' },
              { title: 'Update Skills', icon: '⚡', color: '#ffff00', gradient: 'linear-gradient(45deg, #ffff00, #ff8000)' },
              { title: 'View Messages', icon: '📧', color: '#00ff00', gradient: 'linear-gradient(45deg, #00ff00, #80ff00)' }
            ].map((action, index) => (
              <motion.button
                key={action.title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ 
                  scale: 1.08, 
                  x: 15
                }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (action.title === 'Add New Project') setShowAddProject(true);
                  if (action.title === 'Upload Certificate') setShowAddCertificate(true);
                  if (action.title === 'Add Event Photos') console.log('Add Event Photos clicked');
                  if (action.title === 'Update Skills') setShowUpdateSkills(true);
                }}
                className="admin-quick-action admin-btn"
                style={{
                  padding: '1.2rem',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  textAlign: 'left',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  backdropFilter: 'blur(10px)',
                  border: 'none'
                }}
              >
                {/* Hover effect overlay */}
                <motion.div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: action.gradient,
                    opacity: 0.1,
                    transition: 'left 0.5s ease'
                  }}
                  whileHover={{ left: '0%' }}
                />
                <motion.span 
                  style={{ fontSize: '1.8rem', zIndex: 2 }}
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, delay: index * 0.5 }}
                >
                  {action.icon}
                </motion.span>
                <span style={{ zIndex: 2 }}>{action.title}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );

  const renderProjects = () => (
    <div style={{ padding: '2rem' }}>
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}
      >
        <h2 style={{
          color: 'var(--accent-primary)',
          fontSize: '2rem'
        }}>
          Projects Management
        </h2>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddProject(true)}
          className="admin-btn"
          style={{
            padding: '1rem 2rem',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '1rem',
            border: 'none'
          }}
        >
          + Add New Project
        </motion.button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
        style={{
          borderRadius: '16px',
          padding: '2rem',
          backdropFilter: 'blur(15px)',
          overflow: 'auto'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(0, 255, 255, 0.3)' }}>
              <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Project Name</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Status</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Views</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Last Updated</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {PROJECTS_DATA.map((project, index) => (
              <ProjectRow key={project.id} project={project} index={index} />
            ))}
          </tbody>
        </table>
      </motion.div>
    </div>
  );

  const getSkillLogo = (skillName) => {
    const skill = skillName.toLowerCase();
    const logoMap = {
      'react': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg',
      'javascript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg',
      'python': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg',
      'java': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg',
      'html': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg',
      'css': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg',
      'nodejs': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg',
      'node.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg',
      'mongodb': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg',
      'mysql': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
      'git': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg',
      'github': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg',
      'tailwind': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg',
      'bootstrap': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bootstrap/bootstrap-original.svg',
      'express': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg',
      'firebase': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-original.svg',
      'sass': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/sass/sass-original.svg'
    };
    return logoMap[skill] || `https://cdn.jsdelivr.net/gh/devicons/devicon/icons/${skill}/${skill}-original.svg`;
  };

  const renderUpdateSkills = () => (
    <div style={{ padding: '2rem' }}>
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}
      >
        <h2 style={{
          color: 'var(--accent-primary)',
          fontSize: '2rem'
        }}>
          Update Skills
        </h2>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowUpdateSkills(false)}
          className="admin-btn-secondary"
          style={{
            padding: '0.8rem 1.5rem',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          ← Back
        </motion.button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
        style={{
          borderRadius: '16px',
          padding: '2rem',
          backdropFilter: 'blur(15px)',
          maxWidth: '800px',
          margin: '0 auto'
        }}
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          console.log('New skill:', { ...newSkill, logo: getSkillLogo(newSkill.name) });
          setShowUpdateSkills(false);
          setNewSkill({ name: '', level: '', category: 'Programming Languages' });
        }}>
          <div className="admin-form-group">
            <label className="admin-form-label">Skill Name</label>
            <input
              type="text"
              className="admin-form-input"
              value={newSkill.name}
              onChange={(e) => setNewSkill({...newSkill, name: e.target.value})}
              placeholder="e.g., React, Python, JavaScript"
              required
            />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Completion Percentage</label>
            <input
              type="number"
              className="admin-form-input"
              min="0"
              max="100"
              value={newSkill.level}
              onChange={(e) => setNewSkill({...newSkill, level: e.target.value})}
              placeholder="e.g., 85"
              required
            />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Category</label>
            <select
              className="admin-form-input"
              value={newSkill.category}
              onChange={(e) => setNewSkill({...newSkill, category: e.target.value})}
            >
              <option value="Programming Languages">Programming Languages</option>
              <option value="Tools/Frameworks">Tools/Frameworks</option>
            </select>
          </div>

          {newSkill.name && (
            <div className="admin-form-group">
              <label className="admin-form-label">Auto-fetched Logo Preview</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <img 
                  src={getSkillLogo(newSkill.name)} 
                  alt={newSkill.name}
                  width="32" 
                  height="32"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/32x32/666/fff?text=?';
                  }}
                />
                <span style={{ color: 'var(--text-primary)' }}>{newSkill.name}</span>
              </div>
            </div>
          )}

          <motion.button
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="admin-btn"
            style={{
              width: '100%',
              padding: '1rem',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              marginTop: '1rem'
            }}
          >
            Add Skill
          </motion.button>
        </form>
      </motion.div>
    </div>
  );

  const renderAddCertificate = () => (
    <div style={{ padding: '2rem' }}>
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}
      >
        <h2 style={{
          color: 'var(--accent-primary)',
          fontSize: '2rem'
        }}>
          Upload Certificate
        </h2>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddCertificate(false)}
          className="admin-btn-secondary"
          style={{
            padding: '0.8rem 1.5rem',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          ← Back
        </motion.button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
        style={{
          borderRadius: '16px',
          padding: '2rem',
          backdropFilter: 'blur(15px)',
          maxWidth: '800px',
          margin: '0 auto'
        }}
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          console.log('New certificate:', newCertificate);
          setShowAddCertificate(false);
          setNewCertificate({ name: '', issuer: '', month: '', year: '', photo: null });
        }}>
          <div className="admin-form-group">
            <label className="admin-form-label">Certificate Name</label>
            <input
              type="text"
              className="admin-form-input"
              value={newCertificate.name}
              onChange={(e) => setNewCertificate({...newCertificate, name: e.target.value})}
              required
            />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Issuer</label>
            <input
              type="text"
              className="admin-form-input"
              value={newCertificate.issuer}
              onChange={(e) => setNewCertificate({...newCertificate, issuer: e.target.value})}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="admin-form-group">
              <label className="admin-form-label">Month</label>
              <select
                className="admin-form-input"
                value={newCertificate.month}
                onChange={(e) => setNewCertificate({...newCertificate, month: e.target.value})}
                required
              >
                <option value="" disabled>Select Month</option>
                <option value="January">January</option>
                <option value="February">February</option>
                <option value="March">March</option>
                <option value="April">April</option>
                <option value="May">May</option>
                <option value="June">June</option>
                <option value="July">July</option>
                <option value="August">August</option>
                <option value="September">September</option>
                <option value="October">October</option>
                <option value="November">November</option>
                <option value="December">December</option>
              </select>
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Year</label>
              <input
                type="number"
                className="admin-form-input"
                min="2000"
                max="2030"
                value={newCertificate.year}
                onChange={(e) => setNewCertificate({...newCertificate, year: e.target.value})}
                required
              />
            </div>
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Certificate Photo (JPG/PNG)</label>
            <input
              type="file"
              className="admin-form-input"
              accept=".jpg,.jpeg,.png"
              onChange={(e) => setNewCertificate({...newCertificate, photo: e.target.files[0]})}
              required
              style={{
                padding: '0.6rem',
                cursor: 'pointer'
              }}
            />
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="admin-btn"
            style={{
              width: '100%',
              padding: '1rem',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              marginTop: '1rem'
            }}
          >
            Upload Certificate
          </motion.button>
        </form>
      </motion.div>
    </div>
  );

  const renderAddProject = () => (
    <div style={{ padding: '2rem' }}>
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}
      >
        <h2 style={{
          color: 'var(--accent-primary)',
          fontSize: '2rem'
        }}>
          Add New Project
        </h2>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddProject(false)}
          className="admin-btn-secondary"
          style={{
            padding: '0.8rem 1.5rem',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          ← Back
        </motion.button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
        style={{
          borderRadius: '16px',
          padding: '2rem',
          backdropFilter: 'blur(15px)',
          maxWidth: '800px',
          margin: '0 auto'
        }}
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          console.log('New project:', newProject);
          setShowAddProject(false);
          setNewProject({ title: '', status: 'Coming Soon...', github: '', demo: '', features: ['', '', ''] });
        }}>
          <div className="admin-form-group">
            <label className="admin-form-label">Project Title</label>
            <input
              type="text"
              className="admin-form-input"
              value={newProject.title}
              onChange={(e) => setNewProject({...newProject, title: e.target.value})}
              required
            />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Status</label>
            <select
              className="admin-form-input"
              value={newProject.status}
              onChange={(e) => setNewProject({...newProject, status: e.target.value})}
            >
              <option value="LIVE">LIVE</option>
              <option value="Coming Soon...">Coming Soon...</option>
              <option value="Featured">Featured</option>
            </select>
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">GitHub Link</label>
            <input
              type="url"
              className="admin-form-input"
              value={newProject.github}
              onChange={(e) => setNewProject({...newProject, github: e.target.value})}
              required
            />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Live Demo Link</label>
            <input
              type="url"
              className="admin-form-input"
              value={newProject.demo}
              onChange={(e) => setNewProject({...newProject, demo: e.target.value})}
              required
            />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Features (3)</label>
            {newProject.features.map((feature, index) => (
              <input
                key={index}
                type="text"
                className="admin-form-input"
                placeholder={`Feature ${index + 1}`}
                value={feature}
                onChange={(e) => {
                  const newFeatures = [...newProject.features];
                  newFeatures[index] = e.target.value;
                  setNewProject({...newProject, features: newFeatures});
                }}
                style={{ marginBottom: '0.5rem' }}
                required
              />
            ))}
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="admin-btn"
            style={{
              width: '100%',
              padding: '1rem',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              marginTop: '1rem'
            }}
          >
            Add Project
          </motion.button>
        </form>
      </motion.div>
    </div>
  );

  return (
    <div className="App" data-theme={darkMode ? 'dark' : 'light'} style={{ minHeight: '100vh', position: 'relative' }}>
      <ThreeBackground darkMode={darkMode} />
      <AdminNavbar 
        darkMode={darkMode} 
        toggleDarkMode={() => setDarkMode(!darkMode)}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      
      <div style={{ paddingTop: '80px', position: 'relative', zIndex: 1 }}>
        {showAddProject ? renderAddProject() :
         showAddCertificate ? renderAddCertificate() :
         showUpdateSkills ? renderUpdateSkills() :
          activeTab === 'dashboard' ? renderDashboard() :
          activeTab === 'projects' ? renderProjects() :
          activeTab === 'analytics' ? <Analytics /> :
          activeTab === 'messages' ? <Messages /> :
          activeTab === 'settings' ? <Settings /> : null
        }
      </div>
    </div>
  );
}

export default AdminDashboard;